import React, { useState, useEffect, useCallback } from "react";
import { Users, Plus, Search } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";

import { useContacts } from "src/features/contacts/hooks/useContacts";
import { ContactsTable } from "src/features/contacts/components/ContactsTable";
import { ContactDrawer } from "src/features/contacts/components/ContactDrawer";
import { DeleteContactModal } from "src/features/contacts/components/DeleteContactModal";
import { Can } from "src/components/shared/Can";

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function ContactsPage() {
  const [page, setPage] = useState(1);
  const [searchRaw, setSearch] = useState("");
  const q = useDebounce(searchRaw);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, loading, fetch } = useContacts();

  const currentFilters = useCallback(
    () => ({ page, per_page: 20, q }),
    [page, q]
  );

  useEffect(() => {
    fetch(currentFilters());
  }, [fetch, page, q]);

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setPage(1);
  }, [q]);

  function openCreate() {
    setEditingContact(null);
    setDrawerOpen(true);
  }

  function openEdit(contact) {
    setEditingContact(contact);
    setDrawerOpen(true);
  }

  function openDelete(contact) {
    setDeleteTarget(contact);
  }

  function handleSuccess() {
    fetch(currentFilters());
  }

  const contacts = data?.contacts ?? [];
  const meta = data?.meta;

  return (
    <>
      <div className="px-8 py-7 space-y-5">
        {/* ── Page Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
              <Users className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 tracking-tight">
                Contacts
              </h1>
              <p className="text-[13px] text-slate-500">
                {meta
                  ? `${meta.total} total contact${meta.total !== 1 ? "s" : ""}`
                  : "Manage your buyers and sellers"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Can resource="contacts" action="create">
              <Button
                variant="default"
                size="sm"
                className="gap-2"
                onClick={openCreate}
              >
                <Plus className="h-4 w-4" />
                New Contact
              </Button>
            </Can>
          </div>
        </div>

        {/* ── Filter Bar ── */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search
              style={{ left: "0.75rem" }}
              className="absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
            />
            <Input
              style={{ paddingLeft: "2.5rem" }}
              placeholder="Search name, phone, email…"
              value={searchRaw}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ── Table ── */}
        <ContactsTable
          contacts={contacts}
          loading={loading}
          meta={meta}
          page={page}
          onPageChange={setPage}
          onEdit={openEdit}
          onDelete={openDelete}
          onNewContact={openCreate}
        />
      </div>

      {/* ── Overlays ── */}
      <ContactDrawer
        open={drawerOpen}
        contact={editingContact}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleSuccess}
      />

      <DeleteContactModal
        open={!!deleteTarget}
        contact={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={handleSuccess}
      />
    </>
  );
}
